// A rough, on-device comparison of a recording with the model clip.
//
// Both are turned into a sequence of sound "fingerprints" (MFCCs with their changes, 100 a
// second), normalised so that loudness, microphone and much of the speaker's voice drop out.
// Dynamic time warping then lines the two sequences up, so a slower or faster attempt can still
// match, and the average distance along that alignment becomes a score. A speaking time very
// different from the model's (a missing or extra word, a cut-off) lowers it further.
//
// It is a guide to rhythm and sounds, not a judge of pronunciation: it cannot tell whether each
// word was right, and it barely hears stress. The calibration lives in tests/unit/match.test.ts.

export const SAMPLE_RATE = 16_000;
const FRAME = 400; // 25 ms
const HOP = 160; // 10 ms
const NFFT = 512;
const MELS = 26;
const CEPS = 12; // c1–c12 are computed; c0 (loudness) is left out
const USED = 8; // c1–c8 for matching: the finer detail mostly describes the speaker, not the words

export interface MatchResult {
  /** 0–100, or null when no speech was found in the recording. */
  score: number | null;
  /** Speaking time of the attempt relative to the model (1 = same). */
  pace: number | null;
  /** Average aligned distance, before mapping to a score (for calibration). */
  distance: number | null;
}

// ── Signal helpers ──────────────────────────────────────────────────

function hamming(n: number): Float32Array {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (n - 1));
  return w;
}

/** In-place radix-2 FFT on real/imaginary arrays of length NFFT. */
function fft(re: Float32Array, im: Float32Array) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const ar = re[i + k + len / 2] * cr - im[i + k + len / 2] * ci;
        const ai = re[i + k + len / 2] * ci + im[i + k + len / 2] * cr;
        re[i + k + len / 2] = re[i + k] - ar;
        im[i + k + len / 2] = im[i + k] - ai;
        re[i + k] += ar;
        im[i + k] += ai;
        const t = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = t;
      }
    }
  }
}

const melOf = (hz: number) => 2595 * Math.log10(1 + hz / 700);
const hzOf = (mel: number) => 700 * (10 ** (mel / 2595) - 1);

/** Triangular mel filters between 100 Hz and 7.6 kHz, over the FFT bins. */
function melFilters(): Float32Array[] {
  const lo = melOf(100);
  const hi = melOf(7600);
  const points = Array.from({ length: MELS + 2 }, (_, i) => hzOf(lo + ((hi - lo) * i) / (MELS + 1)));
  const bins = points.map((hz) => Math.floor(((NFFT + 1) * hz) / SAMPLE_RATE));
  return Array.from({ length: MELS }, (_, m) => {
    const f = new Float32Array(NFFT / 2 + 1);
    for (let k = bins[m]; k < bins[m + 1]; k++) f[k] = (k - bins[m]) / Math.max(1, bins[m + 1] - bins[m]);
    for (let k = bins[m + 1]; k < bins[m + 2]; k++) f[k] = (bins[m + 2] - k) / Math.max(1, bins[m + 2] - bins[m + 1]);
    return f;
  });
}

const WINDOW = hamming(FRAME);
const FILTERS = melFilters();
const DCT = Array.from({ length: CEPS }, (_, c) =>
  Float32Array.from({ length: MELS }, (_, m) => Math.cos((Math.PI * (c + 1) * (m + 0.5)) / MELS)),
);

export interface Frames {
  ceps: Float32Array[];
  energy: Float32Array;
}

/** MFCCs (c1–c12) and log-energy per 10 ms frame. */
export function analyse(samples: Float32Array): Frames {
  const count = Math.max(0, Math.floor((samples.length - FRAME) / HOP) + 1);
  const ceps: Float32Array[] = [];
  const energy = new Float32Array(count);
  const re = new Float32Array(NFFT);
  const im = new Float32Array(NFFT);
  const power = new Float32Array(NFFT / 2 + 1);
  const logMel = new Float32Array(MELS);
  for (let f = 0; f < count; f++) {
    re.fill(0);
    im.fill(0);
    const start = f * HOP;
    let e = 0;
    let prev = start > 0 ? samples[start - 1] : 0;
    for (let i = 0; i < FRAME; i++) {
      const x = samples[start + i];
      const pre = x - 0.97 * prev; // pre-emphasis
      prev = x;
      re[i] = pre * WINDOW[i];
      e += x * x;
    }
    energy[f] = 10 * Math.log10(e / FRAME + 1e-10);
    fft(re, im);
    for (let k = 0; k <= NFFT / 2; k++) power[k] = re[k] * re[k] + im[k] * im[k];
    for (let m = 0; m < MELS; m++) {
      let s = 0;
      const filter = FILTERS[m];
      for (let k = 0; k <= NFFT / 2; k++) s += filter[k] * power[k];
      logMel[m] = Math.log(s + 1e-10);
    }
    const c = new Float32Array(CEPS);
    for (let j = 0; j < CEPS; j++) {
      let s = 0;
      for (let m = 0; m < MELS; m++) s += DCT[j][m] * logMel[m];
      c[j] = s;
    }
    ceps.push(c);
  }
  return { ceps, energy };
}

/** The frames that contain speech: from the first to the last loud enough frame, padded a little. */
export function speechSpan(energy: Float32Array): [number, number] | null {
  if (energy.length === 0) return null;
  let peak = -Infinity;
  for (const e of energy) peak = Math.max(peak, e);
  const sorted = Float32Array.from(energy).sort();
  const floor = sorted[Math.floor(sorted.length * 0.1)];
  // Too quiet overall, or no contrast between speech and background: nothing was said.
  if (peak < -55 || peak - floor < 10) return null;
  const threshold = Math.max(peak - 35, floor + 6);
  let first = -1;
  let last = -1;
  for (let i = 0; i < energy.length; i++) {
    if (energy[i] >= threshold) {
      if (first < 0) first = i;
      last = i;
    }
  }
  if (first < 0) return null;
  return [Math.max(0, first - 5), Math.min(energy.length, last + 6)];
}

/**
 * Features for matching, speech frames only: c1–c8 with the utterance mean removed (which takes out
 * the microphone and much of the voice) plus their changes over ±20 ms.
 * Chosen by comparing variants on our clips (tests/unit/experiment.test.ts).
 */
export function fingerprint(samples: Float32Array): Float32Array[] | null {
  const { ceps, energy } = analyse(samples);
  const span = speechSpan(energy);
  if (!span) return null;
  const frames = ceps.slice(span[0], span[1]);
  if (frames.length < 15) return null; // under 0.15 s of speech
  const mean = new Float32Array(USED);
  for (const f of frames) for (let j = 0; j < USED; j++) mean[j] += f[j] / frames.length;
  const norm = frames.map((f) => Float32Array.from({ length: USED }, (_, j) => f[j] - mean[j]));
  return norm.map((f, i) => {
    const a = norm[Math.max(0, i - 2)];
    const b = norm[Math.min(norm.length - 1, i + 2)];
    const out = new Float32Array(USED * 2);
    out.set(f);
    for (let j = 0; j < USED; j++) out[USED + j] = (b[j] - a[j]) / 4;
    return out;
  });
}

/** Dynamic time warping with a band; the average distance per step along the best alignment. */
export function dtwDistance(a: Float32Array[], b: Float32Array[]): number {
  const n = a.length;
  const m = b.length;
  const band = Math.max(Math.abs(n - m) + 5, Math.ceil(0.3 * Math.max(n, m)));
  const INF = Number.POSITIVE_INFINITY;
  let prev = new Float64Array(m + 1).fill(INF);
  let prevLen = new Float64Array(m + 1);
  prev[0] = 0;
  for (let i = 1; i <= n; i++) {
    const cur = new Float64Array(m + 1).fill(INF);
    const curLen = new Float64Array(m + 1);
    const center = Math.round((i * m) / n);
    const from = Math.max(1, center - band);
    const to = Math.min(m, center + band);
    for (let j = from; j <= to; j++) {
      let d = 0;
      const x = a[i - 1];
      const y = b[j - 1];
      for (let k = 0; k < x.length; k++) d += (x[k] - y[k]) ** 2;
      d = Math.sqrt(d);
      // Diagonal steps count twice so paths of different shapes compare fairly; stretching one side
      // costs a little extra, so unrelated phrases can't be matched by warping freely.
      const diag = prev[j - 1] + 2 * d;
      const up = prev[j] + 1.5 * d;
      const left = cur[j - 1] + 1.5 * d;
      if (diag <= up && diag <= left) {
        cur[j] = diag;
        curLen[j] = prevLen[j - 1] + 2;
      } else if (up <= left) {
        cur[j] = up;
        curLen[j] = prevLen[j] + 1;
      } else {
        cur[j] = left;
        curLen[j] = curLen[j - 1] + 1;
      }
    }
    prev = cur;
    prevLen = curLen;
  }
  return prev[m] === INF ? INF : prev[m] / (n + m);
}

/**
 * Distance → 0–100, calibrated on our clips (tests/unit/calibrate.test.ts): the same phrase in the
 * other voice lands around 85–92, a take with a wrong or missing word around 60, a different
 * phrase around 10. Real learners differ from the model more than one synthetic voice from the
 * other, so expect somewhat lower scores for good attempts.
 */
export const CALIBRATION = { center: 31.5, width: 2.3 };

export type MatchLevel = 'very-close' | 'close' | 'partly' | 'different';

export function matchLevel(score: number): MatchLevel {
  if (score >= 80) return 'very-close';
  if (score >= 60) return 'close';
  if (score >= 40) return 'partly';
  return 'different';
}

export function scoreFromDistance(distance: number): number {
  const s = 1 / (1 + Math.exp((distance - CALIBRATION.center) / CALIBRATION.width));
  return Math.round(100 * s);
}

/** Speaking much faster or slower than the model (a word missing or added, a cut-off) costs points. */
export function paceFactor(pace: number): number {
  if (pace >= 0.7 && pace <= 1.6) return 1;
  if (pace < 0.7) return Math.max(0.3, 1 - (0.7 - pace) * 2);
  return Math.max(0.3, 1 - (pace - 1.6) * 0.8);
}

/** Compares an attempt with one or more model clips (every recorded voice of the phrase) and keeps the best match. */
export function match(attempt: Float32Array, models: Float32Array[]): MatchResult {
  const a = fingerprint(attempt);
  if (!a) return { score: null, pace: null, distance: null };
  let best: MatchResult = { score: 0, pace: null, distance: null };
  for (const model of models) {
    const b = fingerprint(model);
    if (!b) continue;
    const distance = dtwDistance(a, b);
    const pace = a.length / b.length;
    const score = Math.round(scoreFromDistance(distance) * paceFactor(pace));
    if (best.score === null || score >= best.score) best = { score, pace, distance };
  }
  return best;
}
